import { useState, useEffect, useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import {
  FolderIcon,
  DocumentTextIcon,
  BookOpenIcon,
  UserGroupIcon,
  CheckCircleIcon,
  DocumentIcon,
  ArrowUpRightIcon,
  ArrowDownRightIcon
} from '@heroicons/react/24/outline';
import AuthContext from '../context/AuthContext';
import axiosInstance from '../utils/axiosConfig';

function Sparkline({ points = [], stroke = '#14b8a6' }) {
  if (!points.length) return null;
  const width = 120;
  const height = 36;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = Math.max(1, max - min);
  const stepX = width / (points.length - 1);
  const d = points
    .map((p, i) => {
      const x = i * stepX;
      const y = height - ((p - min) / range) * height;
      return `${i === 0 ? 'M' : 'L'}${x},${y}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} aria-hidden="true">
      <path d={d} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

const Dashboard = () => {
  const [stats, setStats] = useState({
    categories: 0,
    subcategories: 0,
    posts: 0,
    users: 0
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setError(null);
        const [categories, subcategories, posts, users] = await Promise.all([
          axiosInstance.get(`/categories`),
          axiosInstance.get(`/subcategories`),
          axiosInstance.get(`/posts`),
          axiosInstance.get(`/users`)
        ]);

        setStats({
          categories: categories.data.count || 0,
          subcategories: subcategories.data.count || 0,
          posts: posts.data.count || 0,
          users: users.data.count || 0
        });

        setLoading(false);
      } catch (err) {
        console.error('Dashboard data fetch error:', err);
        
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          toast.error('Authentication error. Please log in again.');
          logout();
          navigate('/login', { replace: true });
        } else {
          setError('Failed to load dashboard data. Please try refreshing the page.');
          toast.error('Error loading dashboard data');
        }
        
        setLoading(false);
      }
    };

    fetchStats();
  }, [logout, navigate]);

  // Simple mock trend data derived from counts (until real timeseries exists)
  const trend = useMemo(() => {
    const safePct = (val) => {
      if (!val || Number.isNaN(val)) return 0;
      return Math.max(-99, Math.min(999, val));
    };
    return {
      posts: { pct: safePct(Math.round((stats.posts % 23) - 5)), up: ((stats.posts % 23) - 5) >= 0 },
      categories: { pct: safePct(Math.round((stats.categories % 17) - 4)), up: ((stats.categories % 17) - 4) >= 0 },
      users: { pct: safePct(Math.round((stats.users % 11) - 3)), up: ((stats.users % 11) - 3) >= 0 },
    };
  }, [stats]);

  const cards = [
    {
      title: 'Total Content',
      count: stats.posts,
      icon: BookOpenIcon,
      color: 'bg-teal-500',
      subtitle: 'Content items',
      trend: trend.posts,
      spark: [2, 3, 2, 5, 7, 6, 8, 9, 7, 10, 12],
      details: [
        { label: '0 published', icon: CheckCircleIcon, color: 'text-green-600' },
        { label: '0 draft', icon: DocumentIcon, color: 'text-gray-500' }
      ]
    },
    {
      title: 'Categories',
      count: stats.categories,
      icon: FolderIcon,
      color: 'bg-teal-500',
      subtitle: `${stats.subcategories} subcategories`,
      trend: trend.categories,
      spark: [1, 1, 2, 3, 2, 4, 3, 5, 6, 5, 7],
      details: []
    },
    {
      title: 'Users',
      count: stats.users,
      icon: UserGroupIcon,
      color: 'bg-teal-500',
      subtitle: 'Active accounts',
      trend: trend.users,
      spark: [5, 4, 6, 7, 6, 9, 10, 9, 11, 12, 13],
      details: []
    }
  ];

  if (loading) {
    return (
      <div>
        <div className="rounded-lg p-6 mb-6" style={{ backgroundColor: '#E0F2F2' }}>
          <div className="h-6 w-40 bg-gray-200 rounded mb-3 animate-pulse" />
          <div className="h-4 w-64 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="p-2 rounded-lg bg-gray-200 animate-pulse h-10 w-10" />
                  <div className="ml-3 h-5 w-24 bg-gray-200 rounded animate-pulse" />
                </div>
              </div>
              <div className="h-8 w-20 bg-gray-200 rounded mb-2 animate-pulse" />
              <div className="h-4 w-28 bg-gray-100 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="text-red-500 mb-4">{error}</div>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Dashboard Header */}
      <div className="rounded-lg p-6 mb-6" style={{ backgroundColor: '#E0F2F2' }}>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Welcome to Lekhan Mala Admin Panel. Here's your content overview.</p>
      </div>

      {/* Main grid: KPIs left, Quick Actions right */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* KPI Cards */}
        <div className="lg:col-span-2">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-2">
            {cards.map((card) => (
              <div key={card.title} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className={`p-2 rounded-lg ${card.color}`}>
                      <card.icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">{card.title}</h3>
                      {card.subtitle && (
                        <p className="text-sm text-gray-500 mt-1">{card.subtitle}</p>
                      )}
                    </div>
                  </div>
                  {card.trend && (
                    <div className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${card.trend.up ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'}`}>
                      {card.trend.up ? (
                        <ArrowUpRightIcon className="h-4 w-4 mr-1" />
                      ) : (
                        <ArrowDownRightIcon className="h-4 w-4 mr-1" />
                      )}
                      {Math.abs(card.trend.pct)}%
                    </div>
                  )}
                </div>

                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-3xl font-bold text-gray-900">{card.count}</div>
                  </div>
                  {card.spark && (
                    <div className="ml-4">
                      <Sparkline points={card.spark} />
                    </div>
                  )}
                </div>

                {card.details.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {card.details.map((detail, index) => (
                      <div key={index} className="flex items-center">
                        <detail.icon className={`h-4 w-4 mr-2 ${detail.color}`} />
                        <span className="text-sm text-gray-600">{detail.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions sidebar */}
        <aside className="lg:sticky lg:top-4">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-4">
              <Link
                to="/categories/add"
                className="block rounded-lg border border-gray-200 bg-white px-4 py-4 shadow-sm hover:shadow transition-shadow focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              >
                <div className="flex items-start">
                  <div className="h-10 w-10 rounded-md bg-teal-50 flex items-center justify-center mr-3">
                    <FolderIcon className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Add Category</div>
                    <div className="text-sm text-gray-500">Create a new category to organize content</div>
                  </div>
                </div>
              </Link>
              <Link
                to="/subcategories/add"
                className="block rounded-lg border border-gray-200 bg-white px-4 py-4 shadow-sm hover:shadow transition-shadow focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              >
                <div className="flex items-start">
                  <div className="h-10 w-10 rounded-md bg-teal-50 flex items-center justify-center mr-3">
                    <DocumentTextIcon className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Add Subcategory</div>
                    <div className="text-sm text-gray-500">Break down categories into specific topics</div>
                  </div>
                </div>
              </Link>
              <Link
                to="/posts/add"
                className="block rounded-lg border border-gray-200 bg-white px-4 py-4 shadow-sm hover:shadow transition-shadow focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              >
                <div className="flex items-start">
                  <div className="h-10 w-10 rounded-md bg-teal-50 flex items-center justify-center mr-3">
                    <BookOpenIcon className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Add Post</div>
                    <div className="text-sm text-gray-500">Publish new content to your readers</div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Dashboard;